import { Component } from "@theme/component";
import fuzzyMatch from "@theme/fuzzy-match";
import {
  parseProvinceFilterKey,
  provinceFilterKey,
  RebateFilterMatcher,
} from "@theme/rebate-filter-match";
import { RebateFilterChips } from "@theme/rebate-filter-chips";
import { RebateFilterSidebar } from "@theme/rebate-filter-sidebar";
import { RebateFilterReveal } from "@theme/rebate-filter-reveal";
import {
  adjustScrollAfterFilter,
  scrollToElement,
} from "@theme/rebate-filter-scroll";
import {
  buildProviderGroups,
  rowProviderGroupKey,
} from "@theme/rebate-list-groups";
import { syncRebateListJsonLd } from "@theme/rebate-list-jsonld";
import {
  parseRebateRegionLabels,
  seedRebateRegionIndexes,
} from "@theme/rebate-region-catalog";
import { normalizeTimingFilterValue } from "@theme/rebate-timing-labels";
import { hydrateProductData, productLabel } from "@theme/product-data-map";
import { rebateRowSearchText } from "@theme/rebate-row-search-text";
import {
  LIVE_FILTER_DEBOUNCE_MS,
  LIVE_FILTER_MIN_CHARS,
} from "@theme/rebate-search-contract";

/** @typedef {import("@theme/rebate-list-types").FilterState} FilterState */
/** @typedef {import("@theme/rebate-list-types").ProviderGroup} ProviderGroup */
/** @typedef {import("@theme/rebate-list-types").RebateRow} RebateRow */
/** @typedef {import("@theme/product-data-map").ProductData} ProductData */

/**
 * @typedef {{
 *   sidebar?: HTMLElement,
 *   grid?: HTMLElement,
 *   chips?: HTMLElement,
 *   searchInput?: HTMLInputElement,
 *   searchAction?: HTMLButtonElement,
 *   desktopSlot?: HTMLElement,
 *   filterTrigger?: HTMLButtonElement,
 *   filterTriggerBadge?: HTMLElement,
 *   filterTriggerLabel?: HTMLElement,
 *   rebateDataScript?: HTMLScriptElement,
 *   productDataScript?: HTMLScriptElement,
 *   regionLabelsScript?: HTMLScriptElement,
 *   filterModal?: HTMLElement & { openModal?: (updateUrl?: boolean, trigger?: HTMLElement | null) => void; closeModal?: () => void },
 *   emptyMessage?: HTMLElement,
 *   resultsStatus?: HTMLElement,
 *   sidebarLoading?: HTMLElement,
 *   regionSection?: HTMLElement,
 *   regionList?: HTMLElement,
 *   regionClear?: HTMLButtonElement,
 *   productSection?: HTMLElement,
 *   productList?: HTMLElement,
 *   productClear?: HTMLButtonElement,
 *   timingSection?: HTMLElement,
 *   timingInputs?: HTMLInputElement[],
 *   timingClear?: HTMLButtonElement,
 *   sidebarClearAll?: HTMLButtonElement,
 *   countryGroupTpl?: HTMLTemplateElement,
 *   checkboxRowTpl?: HTMLTemplateElement,
 *   filterChipTpl?: HTMLTemplateElement,
 *   filterChipClearTpl?: HTMLTemplateElement,
 * }} UtilityRebateListRefs
 */

const DESKTOP_MQ = "(min-width: 992px)";

/** @extends {Component<UtilityRebateListRefs>} */
class UtilityRebateList extends Component {
  /** @type {RebateRow[]} */
  #all = [];

  /** @type {Map<string, RebateRow>} */
  #rebateById = new Map();

  /** @type {HTMLScriptElement | null} */
  #ldScript = null;

  /** @type {MediaQueryList | null} */
  #desktopMql = null;

  /** @type {((e: MediaQueryListEvent) => void) | null} */
  #onMqChange = null;

  /** @type {number} */
  #visibleCount = 0;

  /** @type {FilterState} */
  #state = {
    countries: new Set(),
    provinces: new Set(),
    products: new Set(),
    timing: new Set(),
    query: "",
  };

  /** @type {Map<string, Set<string>>} */
  #provincesByCountry = new Map();

  /** Provinces that appear on at least one rebate row (tri-state expand set). */
  /** @type {Map<string, Set<string>>} */
  #provincesWithOffersByCountry = new Map();

  /** @type {Map<string, string>} */
  #countryNames = new Map();

  /** @type {Map<string, string>} */
  #provinceNames = new Map();

  /**
   * `rowProviderGroupKey + '\\0' + handle` → incentive line ids for that
   * product on the card (rebate + DR siblings after SSR dedupe).
   * @type {Map<string, string[]>}
   */
  #rebateIdsByGroupProduct = new Map();

  /** @type {Map<string, ProductData>} */
  #productData = new Map();

  /** @type {ReturnType<typeof setTimeout> | null} */
  #urlTimer = null;

  /** @type {Set<string>} */
  #queryMatchingRowIds = new Set();

  /** @type {RebateFilterMatcher} */
  #filterMatcher = new RebateFilterMatcher(
    this.#state,
    this.#queryMatchingRowIds,
  );

  /**
   * Debounce timer for the sidebar text-search live filter. Set when the
   * user types in `refs.searchInput`; cleared on disconnect, on submit
   * (so explicit submits don't race the pending debounce), and when a
   * fresh keystroke arrives. Same contract as the hero's `#liveTimer`.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #sidebarLiveTimer = null;

  /** @type {AbortController | null} */
  #filterDialogAbort = null;

  /** @type {RebateFilterReveal} */
  #filterReveal = new RebateFilterReveal();

  /** @type {RebateFilterSidebar} */
  #filterSidebar = new RebateFilterSidebar({
    getRefs: () => this.#ui,
    state: this.#state,
    getAll: () => this.#all,
    getCountryNames: () => this.#countryNames,
    getProvinceNames: () => this.#provinceNames,
    getProvincesByCountry: () => this.#provincesByCountry,
    getProvincesWithOffersByCountry: () => this.#provincesWithOffersByCountry,
    filterMatcher: this.#filterMatcher,
    productLabel: (handle) => this.#productLabel(handle),
    onFilterChange: () => this.#onFilterChange(),
  });

  /** @type {RebateFilterChips} */
  #filterChips = new RebateFilterChips({
    getRefs: () => this.#ui,
    state: this.#state,
    getCountryNames: () => this.#countryNames,
    getProvinceNames: () => this.#provinceNames,
    productLabel: (handle) => this.#productLabel(handle),
    onFilterChange: () => this.#onFilterChange(),
    onClearAll: () => this.clearAll(),
  });

  connectedCallback() {
    super.connectedCallback();

    this.#hydrateProductData();
    this.#hydrateStateFromUrl();
    this.#bindBreakpoint();
    // Reflect any URL-hydrated filter state in the mobile trigger badge,
    // the per-section Clear visibility, the sidebar Clear-all button,
    // and the inline search-action mode before inline rebate data is parsed.
    // The apply-label count is reconciled once `#applyFilter()` runs.
    this.#renderFilterTrigger();
    this.#renderClearAffordances();
    this.#init();

    // Mobile filters apply live while the dialog is open, where scroll
    // correction is intentionally suppressed. Run it once when the sheet
    // closes (Apply, ESC, or backdrop all fire the native `close`) so the
    // buyer lands on the remaining results instead of blank space. The
    // `<dialog>` lives inside `<mysa-modal>`; Component scopes refs to the
    // nearest Component ancestor, so `ref="dialog"` belongs to mysa-modal —
    // query it from filterModal instead of using this.refs.dialog. The
    // same scoping drops sidebar `[ref]` nodes after `#bindBreakpoint`
    // reparents the aside; `#ui` queries those from the host instead.
    this.#filterDialogAbort = new AbortController();
    const filterDialog = this.refs.filterModal?.querySelector("dialog");
    filterDialog?.addEventListener(
      "close",
      () => {
        adjustScrollAfterFilter(this.refs.grid, this.refs.emptyMessage);
      },
      { signal: this.#filterDialogAbort.signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Cancel any in-flight URL sync. `#scheduleUrlSync` debounces history
    // mutations 200ms; without this clear, a filter change made shortly
    // before disconnect would still fire `history.replaceState` against
    // whatever page the user has since navigated to, clobbering its URL.
    if (this.#urlTimer) {
      clearTimeout(this.#urlTimer);
      this.#urlTimer = null;
    }
    if (this.#sidebarLiveTimer != null) {
      clearTimeout(this.#sidebarLiveTimer);
      this.#sidebarLiveTimer = null;
    }
    this.#filterReveal.kill();
    this.#filterDialogAbort?.abort();
    this.#filterDialogAbort = null;
    if (this.#desktopMql && this.#onMqChange) {
      this.#desktopMql.removeEventListener("change", this.#onMqChange);
    }
  }

  /**
   * Delegates to the shared `@theme/product-data-map` helper so the hero
   * search and the list-page card grid stay in lock-step on parsing rules.
   */
  #hydrateProductData() {
    this.#productData = hydrateProductData(
      this.refs.productDataScript,
      "utility-rebate-list",
    );
  }

  /**
   * Resolve a product handle to its storefront title; falls back to the
   * handle when no mapping is available.
   * @param {string} handle
   * @returns {string}
   */
  #productLabel(handle) {
    return productLabel(this.#productData, handle);
  }

  /**
   * Parse Liquid-inlined rebate rows from v2 utility_program families.
   * @returns {{ results?: unknown[] } | null}
   */
  #loadInlineEnvelope() {
    const inlineScript = this.refs.rebateDataScript;
    if (!inlineScript?.textContent?.trim()) {
      console.warn("[utility-rebate-list] missing inline rebate data");
      return null;
    }

    try {
      const parsed = JSON.parse(inlineScript.textContent);
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch (err) {
      console.warn(
        "[utility-rebate-list] failed to parse inline rebate data",
        err,
      );
    }

    return null;
  }

  #init() {
    // Inline JSON is the only data source (no endpoint/fixture fallback), so a
    // missing or malformed envelope must still fall through to the normal
    // render path — otherwise the sidebar is stuck on "Loading…", the search
    // controls stay disabled, and no empty state ever shows.
    const envelope = this.#loadInlineEnvelope();
    this.#all =
      envelope &&
      typeof envelope === "object" &&
      Array.isArray(envelope.results)
        ? envelope.results
        : [];
    this.#indexRebatesById();
    this.#indexProvinces();
    this.#indexRegionNames();
    this.#rebateIdsByGroupProduct =
      RebateFilterMatcher.indexRebateIdsByGroupProduct(this.#all);
    // URL hydrate can arrive before the province index exists; expand any
    // legacy bare province codes (`CA`) into country-scoped keys (`US:CA`).
    this.#normalizeProvinceFilters();
    // Country checkbox means "all regions"; drop child provinces that would
    // otherwise AND-narrow a hydrated country facet (sidebar does the same).
    this.#normalizeCountryProvinceOverlap();
    this.#updateQueryMatchCache();
    if (!this.#hasPrerenderedCards()) {
      console.warn(
        "[utility-rebate-list] no server-rendered cards found; grid will stay empty",
      );
    }
    this.#filterSidebar.render();
    // Never scroll on initial load — scroll adjustment is a filter-change
    // affordance, and firing it here would jump the page down to the grid.
    // `#applyFilter` also syncs ItemList JSON-LD to the visible set.
    this.#applyFilter({ adjustScroll: false });
    this.#filterChips.render();
    if (this.#ui.searchInput) this.#ui.searchInput.disabled = false;
    if (this.#ui.searchAction) this.#ui.searchAction.disabled = false;
    this.#pushDl("rebate_list_view");
  }

  #indexRebatesById() {
    this.#rebateById = new Map();
    for (const row of this.#all) {
      if (row.id) this.#rebateById.set(row.id, row);
    }
  }

  #indexProvinces() {
    this.#provincesByCountry = new Map();
    this.#provincesWithOffersByCountry = new Map();
    this.#countryNames = new Map();
    this.#provinceNames = new Map();
    // Full CA/US catalogs from locale labels so zero-count regions still appear.
    seedRebateRegionIndexes(
      this.#countryNames,
      this.#provinceNames,
      this.#provincesByCountry,
      provinceFilterKey,
      this.#readRegionLabels(),
    );
    // Offer set is separate — tri-state expand/collapse must not use the
    // full static catalog or "country minus one" explodes into ~55 URL keys.
    for (const row of this.#all) {
      const cc = row.region?.country?.code;
      const pc = row.region?.province?.code;
      if (!cc || !pc) continue;
      if (!this.#provincesWithOffersByCountry.has(cc)) {
        this.#provincesWithOffersByCountry.set(cc, new Set());
      }
      this.#provincesWithOffersByCountry.get(cc)?.add(pc);
      // Live rows may introduce regions outside the locale catalog.
      if (!this.#provincesByCountry.has(cc)) {
        this.#provincesByCountry.set(cc, new Set());
      }
      this.#provincesByCountry.get(cc)?.add(pc);
    }
  }

  /**
   * Locale labels from `snippets/utility-rebate-region-labels.liquid`.
   * @returns {import("@theme/rebate-region-catalog").RebateRegionLabels}
   */
  #readRegionLabels() {
    const script = this.refs.regionLabelsScript;
    if (!(script instanceof HTMLScriptElement)) return {};
    try {
      return parseRebateRegionLabels(JSON.parse(script.textContent || "{}"));
    } catch {
      return {};
    }
  }

  /**
   * Fill gaps only — never overwrite locale-seeded labels with Attio/English
   * names from live rebate rows.
   */
  #indexRegionNames() {
    for (const row of this.#all) {
      const cc = row.region?.country?.code;
      const cn = row.region?.country?.name;
      if (cc && !this.#countryNames.has(cc)) {
        this.#countryNames.set(cc, cn || cc);
      }
      const pc = row.region?.province?.code;
      const pn = row.region?.province?.name;
      if (cc && pc) {
        const key = provinceFilterKey(cc, pc);
        if (!this.#provinceNames.has(key)) {
          this.#provinceNames.set(key, pn || pc);
        }
      }
    }
  }

  /**
   * Coerce `#state.provinces` to country-scoped keys (`CC:PC`). Bare codes from
   * older shared URLs expand to every matching country in the current catalog.
   */
  #normalizeProvinceFilters() {
    if (!this.#state.provinces.size) return;

    /** @type {Set<string>} */
    const next = new Set();
    for (const key of this.#state.provinces) {
      if (key.includes(":")) {
        next.add(key);
        continue;
      }
      for (const [cc, provinces] of this.#provincesByCountry) {
        if (provinces.has(key)) next.add(provinceFilterKey(cc, key));
      }
    }
    this.#state.provinces.clear();
    for (const scoped of next) this.#state.provinces.add(scoped);
  }

  /**
   * When a country facet is selected, remove that country's province keys so
   * nested sidebar / shared-URL state cannot AND country with its children.
   */
  #normalizeCountryProvinceOverlap() {
    if (!this.#state.countries.size || !this.#state.provinces.size) return;
    for (const key of [...this.#state.provinces]) {
      const { country, province } = parseProvinceFilterKey(key);
      if (!this.#state.countries.has(country)) continue;
      const known = this.#provincesByCountry.get(country);
      if (known?.has(province)) this.#state.provinces.delete(key);
    }
  }

  /**
   * Cards are server-rendered in Liquid; JS only toggles visibility.
   * @returns {boolean}
   */
  #hasPrerenderedCards() {
    return Boolean(this.refs.grid?.querySelector("[data-provider-card]"));
  }

  #hydrateStateFromUrl() {
    const p = new URLSearchParams(window.location.search);
    const map = [
      ["country", this.#state.countries],
      ["province", this.#state.provinces],
      ["products", this.#state.products],
      ["timing", this.#state.timing],
    ];
    for (const [key, set] of map) {
      const raw = p.get(key);
      if (!raw) continue;
      for (const v of raw.split(",")) {
        if (!v) continue;
        const normalized = key === "timing" ? normalizeTimingFilterValue(v) : v;
        set.add(normalized);
      }
    }
    const q = p.get("q");
    if (q) {
      // Match live filter / submit: sub-threshold queries must not filter the
      // list. Still mirror the text into the input so shared/bookmarked short
      // `q` values remain editable (same as typing below the threshold).
      const trimmed = q.trim();
      if (trimmed.length >= LIVE_FILTER_MIN_CHARS) {
        this.#state.query = trimmed;
      }
      if (this.#ui.searchInput) this.#ui.searchInput.value = q;
    }
  }

  /**
   * Pre-compute the set of row IDs that match the current query string.
   * Called once per filter change to avoid redundant fuzzyMatch calls when
   * computing sidebar facet counts (matching rows → unique cards).
   */
  #updateQueryMatchCache() {
    this.#queryMatchingRowIds.clear();
    const q = this.#state.query.trim();
    if (!q) {
      for (const row of this.#all) {
        this.#queryMatchingRowIds.add(row.id);
      }
      return;
    }
    const scored = fuzzyMatch(q, this.#all, (item) =>
      this.#rowSearchText(/** @type {RebateRow} */ (item)),
    );
    for (const { item } of scored) {
      this.#queryMatchingRowIds.add(/** @type {RebateRow} */ (item).id);
    }
  }

  /**
   * Combined haystack used for fuzzy search per row. Delegates to the shared
   * `@theme/rebate-row-search-text` module so this list-page search and the
   * hero `<utility-search-bar>` index the same fields (raw handles + resolved
   * titles), keeping queries like "smart", "legacy", and "in-floor" symmetric
   * across both entry points.
   *
   * @param {RebateRow} row
   * @returns {string}
   */
  #rowSearchText(row) {
    return rebateRowSearchText(row, (handle) => this.#productLabel(handle));
  }

  /**
   * @param {HTMLElement} cell
   * @param {boolean} show
   */
  #setCellVisible(cell, show) {
    cell.toggleAttribute("hidden", !show);
    cell.classList.toggle("utility-card--match", show);
  }

  /**
   * @param {HTMLElement} cell
   * @returns {{ show: boolean }}
   */
  #applyProductRowVisibility(cell) {
    const productRows = cell.querySelectorAll("[data-product-row]");
    let providerVisible = false;
    /** @type {Element[]} */
    const visibleProductRows = [];

    for (const productRow of productRows) {
      const id = productRow.getAttribute("data-rebate-id");
      const handle = productRow.getAttribute("data-product-handle") ?? "";
      const row = id ? this.#rebateById.get(id) : undefined;
      // Sibling incentive lines share one DOM row after SSR dedupe — OR
      // their match results via the hydrate-time group+handle index.
      const siblingIds = row
        ? (this.#rebateIdsByGroupProduct.get(
            `${rowProviderGroupKey(row)}\0${handle}`,
          ) ?? [row.id])
        : [];
      const show = this.#filterMatcher.productRowMatchesDisplayInGroup(
        row,
        handle,
        siblingIds,
        this.#rebateById,
      );
      productRow.toggleAttribute("hidden", !show);
      productRow.classList.remove(
        "utility-card__product--first-visible",
        "utility-card__product--last-visible",
        "utility-card__product--only-visible",
      );
      if (!show || !row) continue;
      providerVisible = true;
      visibleProductRows.push(productRow);
    }

    if (visibleProductRows.length > 0) {
      visibleProductRows[0].classList.add(
        "utility-card__product--first-visible",
      );
      visibleProductRows[visibleProductRows.length - 1].classList.add(
        "utility-card__product--last-visible",
      );
      if (visibleProductRows.length === 1) {
        visibleProductRows[0].classList.add(
          "utility-card__product--only-visible",
        );
      }
    }

    return { show: providerVisible };
  }

  /**
   * @param {number} visibleRows
   */
  #updateEmptyMessage(visibleRows) {
    const empty = this.refs.emptyMessage;
    if (!(empty instanceof HTMLElement)) return;

    empty.textContent =
      this.#all.length === 0
        ? (window.Theme?.translations?.rebates_list_empty_initial ??
          window.Theme?.translations?.rebates_list_empty ??
          "")
        : (window.Theme?.translations?.rebates_list_empty ?? "");

    empty.hidden = visibleRows > 0;

    const status = this.refs.resultsStatus;
    if (status instanceof HTMLElement) {
      if (visibleRows > 0) {
        const t = window.Theme?.translations ?? {};
        const tpl =
          visibleRows === 1
            ? (t.rebates_filters_search_count_singular ?? "{{ count }} rebate")
            : (t.rebates_filters_search_count_plural ?? "{{ count }} rebates");
        status.textContent = tpl.replace(
          /\{\{\s*count\s*\}\}/,
          String(visibleRows),
        );
      } else {
        status.textContent = "";
      }
    }
  }

  #applyFilter({ animate = false, adjustScroll = true } = {}) {
    const grid = this.refs.grid;
    if (!grid) return;

    const cells = [...grid.querySelectorAll("[data-provider-card]")].filter(
      (cell) => cell instanceof HTMLElement,
    );

    /** @type {{ cell: HTMLElement; show: boolean; wasShown: boolean }[]} */
    const cellStates = cells.map((cell) => {
      const { show } = this.#applyProductRowVisibility(cell);
      return {
        cell,
        show,
        wasShown: !cell.hasAttribute("hidden"),
      };
    });

    /** @type {HTMLElement[]} */
    const toReveal = [];
    let visibleCards = 0;

    for (const { cell, show, wasShown } of cellStates) {
      if (show) visibleCards += 1;
      if (wasShown && !show) {
        this.#setCellVisible(cell, false);
      } else if (!wasShown && show) {
        toReveal.push(cell);
      } else {
        this.#setCellVisible(cell, show);
      }
    }

    // Match sidebar facet badges: count cards, not incentive lines.
    this.#visibleCount = visibleCards;
    this.#updateEmptyMessage(visibleCards);
    this.#renderFilterTrigger();
    this.#renderApplyLabel();
    this.#syncJsonLd();

    if (toReveal.length > 0) {
      for (const cell of toReveal) {
        this.#setCellVisible(cell, true);
      }
      if (animate) {
        this.#filterReveal.animateIn(toReveal);
      }
    }

    if (adjustScroll) {
      adjustScrollAfterFilter(this.refs.grid, this.refs.emptyMessage);
    }
  }

  /**
   * Keep ItemList JSON-LD aligned with the filtered grid (including empty).
   */
  #syncJsonLd() {
    const visibleRows = this.#all.filter((row) =>
      this.#filterMatcher.rowMatchesDisplay(row),
    );
    this.#ldScript = syncRebateListJsonLd(
      this,
      buildProviderGroups(visibleRows),
      this.#ldScript,
      visibleRows.length > 0,
    );
  }

  /* ============================================================
   * Mobile filter dialog
   * ============================================================ */

  /**
   * Total number of filters currently active across countries, provinces,
   * products, timing, and the free-text query. Used to badge the mobile
   * filter trigger ("Filters (3)") so merchants can see at a glance how many
   * facets are applied without opening the dialog.
   * @returns {number}
   */
  #activeFilterCount() {
    let count =
      this.#state.countries.size +
      this.#state.provinces.size +
      this.#state.products.size +
      this.#state.timing.size;
    if (this.#state.query.trim()) count += 1;
    return count;
  }

  #renderFilterTrigger() {
    const trigger = this.refs.filterTrigger;
    if (!trigger) return;
    const count = this.#activeFilterCount();
    const t = window.Theme?.translations ?? {};

    if (this.refs.filterTriggerLabel) {
      const tpl = count > 0 ? t.rebates_filters_open_with_count : null;
      this.refs.filterTriggerLabel.textContent = tpl
        ? tpl.replace(/\{\{\s*count\s*\}\}/, String(count))
        : (t.rebates_filters_open ?? "Filters");
    }

    const badge = this.refs.filterTriggerBadge;
    if (badge) {
      if (count > 0) {
        badge.textContent = String(count);
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }
  }

  #renderApplyLabel() {
    const apply =
      this.querySelector(".utility-filter-modal__apply .button-text") ??
      this.querySelector(".utility-filter-modal__apply");
    if (!apply) return;
    const t = window.Theme?.translations ?? {};
    const tpl =
      this.#visibleCount === 1
        ? (t.rebates_filters_show_count_singular ?? "Show {{ count }} rebate")
        : (t.rebates_filters_show_count_plural ?? "Show {{ count }} rebates");
    apply.textContent = tpl.replace(
      /\{\{\s*count\s*\}\}/,
      String(this.#visibleCount),
    );
  }

  /* ============================================================
   * Public handlers (bound via `on:` attributes in Liquid)
   * ============================================================ */

  openDialog() {
    this.refs.filterModal?.openModal?.(true, this.refs.filterTrigger);
  }

  closeDialog() {
    this.refs.filterModal?.closeModal?.();
  }

  clearAll() {
    this.#state.countries.clear();
    this.#state.provinces.clear();
    this.#state.products.clear();
    this.#state.timing.clear();
    this.#state.query = "";
    if (this.#ui.searchInput) this.#ui.searchInput.value = "";
    // Hero search is a separate custom element — clear it so Clear-all /
    // chip dismiss stay in sync when the list was filtered from above.
    document
      .querySelectorAll("utility-search-bar")
      .forEach((bar) => bar.clearQuery?.());
    this.#onFilterChange();
    this.closeDialog();
  }

  /**
   * Per-section Clear handlers. Each clears the relevant `#state` Sets,
   * funnels through `#onFilterChange()` for a single re-render pass, and
   * exits silently when the section is already empty — keeps idle
   * clicks from queueing redundant render work or DL events.
   *
   * Bound via `on:click="utility-rebate-list/clear<Section>"` on the
   * per-section Clear buttons in the sidebar markup.
   */
  clearRegion() {
    if (!this.#state.countries.size && !this.#state.provinces.size) return;
    this.#state.countries.clear();
    this.#state.provinces.clear();
    this.#onFilterChange();
  }

  clearProducts() {
    if (!this.#state.products.size) return;
    this.#state.products.clear();
    this.#onFilterChange();
  }

  clearTiming() {
    if (!this.#state.timing.size) return;
    this.#state.timing.clear();
    this.#onFilterChange();
  }

  /**
   * Component refs are owned by the nearest Component ancestor. Below 992px
   * we reparent the sidebar into `<mysa-modal>`, so every `[ref]` inside the
   * aside drops off `this.refs` on the next mutation pass — Select All /
   * Clear all then update filter state but cannot re-render checkboxes.
   * Querying the host light DOM skips that scoping; the nodes still live
   * under this element.
   * @returns {UtilityRebateListRefs}
   */
  get #ui() {
    /**
     * @param {string} name
     * @returns {Element | null}
     */
    const q = (name) => this.querySelector(`[ref="${CSS.escape(name)}"]`);
    /**
     * @param {string} name
     * @returns {Element[]}
     */
    const qAll = (name) => [
      ...this.querySelectorAll(`[ref="${CSS.escape(name)}[]"]`),
    ];

    return /** @type {UtilityRebateListRefs} */ ({
      ...this.refs,
      sidebar: q("sidebar") ?? undefined,
      sidebarLoading: q("sidebarLoading") ?? undefined,
      searchInput: q("searchInput") ?? undefined,
      searchAction: q("searchAction") ?? undefined,
      regionSection: q("regionSection") ?? undefined,
      regionList: q("regionList") ?? undefined,
      regionClear: q("regionClear") ?? undefined,
      productSection: q("productSection") ?? undefined,
      productList: q("productList") ?? undefined,
      productClear: q("productClear") ?? undefined,
      timingSection: q("timingSection") ?? undefined,
      timingInputs: qAll("timingInputs"),
      timingClear: q("timingClear") ?? undefined,
      sidebarClearAll: q("sidebarClearAll") ?? undefined,
      countryGroupTpl: q("countryGroupTpl") ?? undefined,
      checkboxRowTpl: q("checkboxRowTpl") ?? undefined,
      filterChipTpl: q("filterChipTpl") ?? undefined,
      filterChipClearTpl: q("filterChipClearTpl") ?? undefined,
    });
  }

  /**
   * Move the sidebar between the inline (desktop) and dialog (mobile) slot
   * based on viewport width. Keeps a single `<aside>` instance — one set of
   * inputs, one set of listeners, one source of truth for facet state.
   */
  #bindBreakpoint() {
    if (!this.#ui.sidebar) return;
    this.#desktopMql = window.matchMedia(DESKTOP_MQ);
    this.#applySlotForBreakpoint();
    this.#onMqChange = () => this.#applySlotForBreakpoint();
    this.#desktopMql.addEventListener("change", this.#onMqChange);
  }

  #applySlotForBreakpoint() {
    const sidebar = this.#ui.sidebar;
    if (!sidebar) return;
    const isDesktop = this.#desktopMql?.matches ?? true;
    const mobileSlot = this.querySelector("[data-utility-filter-mobile-slot]");
    const target = isDesktop ? this.refs.desktopSlot : mobileSlot;
    if (!target) return;
    if (sidebar.parentElement !== target) {
      target.appendChild(sidebar);
    }
    // Closing on crossover avoids a stuck-open dialog when expanding past 992px.
    if (isDesktop) this.closeDialog();
  }

  #onFilterChange({ adjustScroll = true } = {}) {
    this.#updateQueryMatchCache();
    this.#filterSidebar.render();
    this.#applyFilter({ animate: true, adjustScroll });
    this.#filterChips.render();
    this.#renderClearAffordances();
    this.#scheduleUrlSync();
    this.#pushDl("rebate_filter_change");
  }

  /**
   * Sync the visibility of the per-section Clear buttons + the bottom
   * Clear-all rail, and the inline search-action button's mode/aria-
   * label, against the current `#state`. Called from `#onFilterChange()`
   * and from `connectedCallback()` so URL-hydrated state lights up the
   * right affordances on first paint.
   */
  #renderClearAffordances() {
    const hasRegion =
      this.#state.countries.size > 0 || this.#state.provinces.size > 0;
    const hasProducts = this.#state.products.size > 0;
    const hasTiming = this.#state.timing.size > 0;
    const hasQuery = this.#state.query.trim().length > 0;

    // Per-section Clear stays visible; idle sections are greyed + disabled.
    this.#setSectionClearEnabled(this.#ui.regionClear, hasRegion);
    this.#setSectionClearEnabled(this.#ui.productClear, hasProducts);
    this.#setSectionClearEnabled(this.#ui.timingClear, hasTiming);

    const anyActive = hasRegion || hasProducts || hasTiming || hasQuery;
    // Desktop Clear-all animates via `.is-active` (mobile modal owns its own).
    if (this.#ui.sidebarClearAll) {
      this.#ui.sidebarClearAll.classList.toggle("is-active", anyActive);
      this.#ui.sidebarClearAll.disabled = !anyActive;
      this.#ui.sidebarClearAll.setAttribute(
        "aria-disabled",
        anyActive ? "false" : "true",
      );
    }

    this.#updateSearchActionMode();
  }

  /**
   * @param {HTMLButtonElement | undefined} button
   * @param {boolean} enabled
   */
  #setSectionClearEnabled(button, enabled) {
    if (!button) return;
    button.hidden = false;
    button.disabled = !enabled;
    button.setAttribute("aria-disabled", enabled ? "false" : "true");
    button.classList.toggle(
      "utility-filter-sidebar__section-clear--idle",
      !enabled,
    );
  }

  /**
   * Flip the inline action button between `search` (commit-the-query,
   * including empty, for keyboard symmetry with Enter) and `clear`
   * (reset the value + committed query) based on what's currently in
   * the input. Also swaps the aria-label so screen readers track the
   * mode change.
   */
  #updateSearchActionMode() {
    const button = this.#ui.searchAction;
    const input = this.#ui.searchInput;
    if (!button || !input) return;
    const mode = input.value.length > 0 ? "clear" : "search";
    if (button.dataset.mode !== mode) {
      button.dataset.mode = mode;
    }
    const t = window.Theme?.translations ?? {};
    const label =
      mode === "clear"
        ? (t.rebates_filters_search_action_clear ?? "Clear")
        : (t.rebates_filters_search_action_search ?? "Search");
    if (button.getAttribute("aria-label") !== label) {
      button.setAttribute("aria-label", label);
    }
  }

  /**
   * Scroll the rebate grid's top edge into view. Honours
   * `prefers-reduced-motion` by jumping instantly when the user has opted
   * out of motion, otherwise smooth-scrolls. Bails out cleanly when the
   * grid ref isn't available (e.g. mid-disconnect).
   */
  #scrollToGrid() {
    scrollToElement(this.refs.grid, { smooth: true });
  }

  /**
   * URL sync contract: this method may ONLY add, mutate, or remove the
   * filter-related params listed in `FILTER_KEYS` below. Every other param
   * on the URL — `preview_theme_id`, `view`, theme-editor preview tokens,
   * UTM tags, etc. — is preserved exactly. Future edits MUST keep this
   * contract intact; reaching for `URLSearchParams()` (empty ctor) or
   * `window.location.pathname` without `window.location.search` will strip
   * the merchant's preview env every time a checkbox is toggled.
   */
  #scheduleUrlSync() {
    if (this.#urlTimer) clearTimeout(this.#urlTimer);
    this.#urlTimer = setTimeout(() => {
      const FILTER_KEYS = ["q", "country", "province", "products", "timing"];
      const url = new URL(window.location.href);
      for (const key of FILTER_KEYS) {
        url.searchParams.delete(key);
      }
      if (this.#state.countries.size) {
        url.searchParams.set("country", [...this.#state.countries].join(","));
      }
      if (this.#state.provinces.size) {
        url.searchParams.set("province", [...this.#state.provinces].join(","));
      }
      if (this.#state.products.size) {
        url.searchParams.set("products", [...this.#state.products].join(","));
      }
      if (this.#state.timing.size) {
        url.searchParams.set("timing", [...this.#state.timing].join(","));
      }
      const q = this.#state.query.trim();
      if (q) url.searchParams.set("q", q);
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }, 200);
  }

  /* ============================================================
   * Text search input
   *
   * Submit-driven: typing the input does NOT touch filter state.
   * Pressing Enter, or clicking the inline action button while it's in
   * `search` mode, commits the trimmed value as `#state.query` and
   * smooth-scrolls to the top of the grid so the user lands on the
   * freshly-filtered results. While the input has any text, the action
   * button flips to `clear` mode and a click resets the input + the
   * committed query.
   *
   * The action button's mode (`search` vs `clear`) tracks the input's
   * value via the `on:input` handler below and is also reconciled after
   * every filter change in `#updateSearchActionMode()`.
   * ============================================================ */

  /**
   * Bound via `on:keydown` in Liquid. Enter commits the typed query; every
   * other key is ignored so typing/arrow-keying inside the input stays
   * unmolested.
   * @param {KeyboardEvent} e
   */
  handleSearchKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      this.submitSearch();
    }
  }

  /**
   * Bound via `on:input` in Liquid. Keeps the inline action button's
   * mode (`search` vs `clear`) in sync with the input value, then
   * schedules a debounced live-filter push so the list narrows as the
   * user types — same contract as the hero search bar.
   */
  handleSearchInput() {
    this.#updateSearchActionMode();
    this.#scheduleSidebarLiveFilter();
  }

  /**
   * Bound via `on:click` on the inline action button in Liquid. Branches
   * on the button's current mode:
   *   - `clear`: blank the input and commit the empty query, resetting
   *     the list. Cancels any pending live-filter debounce so a stale
   *     timer can't immediately re-apply the previous query.
   *   - `search`: commit whatever's in the input (may be empty) and
   *     scroll to the grid — matches Enter-on-empty for keyboard parity.
   */
  handleSearchActionClick() {
    const button = this.#ui.searchAction;
    if (!button || button.disabled) return;
    if (button.dataset.mode === "clear") {
      if (this.#sidebarLiveTimer != null) {
        clearTimeout(this.#sidebarLiveTimer);
        this.#sidebarLiveTimer = null;
      }
      if (this.#ui.searchInput) this.#ui.searchInput.value = "";
      this.setQuery("", { scroll: false });
      return;
    }
    this.submitSearch();
  }

  /**
   * Commit the input's current value as the search query and scroll the
   * results into view. Flushes any pending live-filter debounce first so
   * an in-flight scheduled push can't squash the explicit submit. Honors
   * the same {@link LIVE_FILTER_MIN_CHARS} threshold as live
   * filtering — sub-threshold submits still scroll (the user clearly
   * asked to see the list) but don't apply a noisy 1–2 char query.
   */
  submitSearch() {
    if (this.#sidebarLiveTimer != null) {
      clearTimeout(this.#sidebarLiveTimer);
      this.#sidebarLiveTimer = null;
    }
    this.#applySidebarLiveFilter({ scroll: true });
  }

  /**
   * Same-page mirror of the hero's debounced live-filter push. Each
   * keystroke replaces the pending timer; below
   * {@link LIVE_FILTER_MIN_CHARS} chars the filter is cleared
   * so deletions revert the list.
   */
  #scheduleSidebarLiveFilter() {
    if (this.#sidebarLiveTimer != null) clearTimeout(this.#sidebarLiveTimer);
    this.#sidebarLiveTimer = setTimeout(() => {
      this.#sidebarLiveTimer = null;
      this.#applySidebarLiveFilter({ scroll: false });
    }, LIVE_FILTER_DEBOUNCE_MS);
  }

  /**
   * Read the current input value, apply the threshold guard, and route
   * through `setQuery` so the public API remains the single mutation
   * path for `#state.query`. Both the debounced live filter and the
   * explicit submit funnel through here.
   *
   * When the trimmed value is below the threshold, we clear the query
   * state (so the filter isn't applied) but preserve the input text
   * (so the user's typed characters don't vanish on submit). Above-
   * threshold queries use the normal `setQuery` path which syncs the
   * input and state together.
   * @param {{ scroll: boolean }} opts
   */
  #applySidebarLiveFilter({ scroll }) {
    const input = this.#ui.searchInput;
    if (!input) return;
    const value = input.value.trim();

    if (value.length >= LIVE_FILTER_MIN_CHARS) {
      this.setQuery(value, { scroll });
    } else {
      if (this.#state.query !== "") {
        this.#state.query = "";
        // Passive reset (typing below the threshold) must not move the page;
        // only an explicit submit scrolls, via #scrollToGrid below.
        this.#onFilterChange({ adjustScroll: false });
      }
      if (scroll) this.#scrollToGrid();
    }
  }

  /**
   * Public entry point for external controllers (e.g. the hero
   * `<utility-search-bar>` on the same page). Updates the search state,
   * mirrors the value into the sidebar input so the UI stays in sync,
   * re-runs the filter pipeline only if the trimmed value actually
   * changed, and optionally scrolls the grid into view.
   *
   * Always-scroll-on-submit semantics are preserved by callers passing
   * `{ scroll: true }` regardless of whether the value changed.
   *
   * @param {string} query
   * @param {{ scroll?: boolean }} [opts]
   */
  setQuery(query, { scroll = false } = {}) {
    const next = (query ?? "").trim();
    if (this.#state.query !== next) {
      this.#state.query = next;
      if (this.#ui.searchInput && this.#ui.searchInput.value !== next) {
        this.#ui.searchInput.value = next;
      }
      // Search never uses the shrink-to-results correction: explicit submits
      // scroll via #scrollToGrid below, and live/passive updates must leave the
      // page still as the buyer types.
      this.#onFilterChange({ adjustScroll: false });
    }
    if (scroll) this.#scrollToGrid();
  }

  /**
   * @param {string} event
   * @param {Record<string, string>} [payload]
   */
  #pushDl(event, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }
}

if (!customElements.get("utility-rebate-list")) {
  customElements.define("utility-rebate-list", UtilityRebateList);
}
